import { useEffect, useState, lazy, Suspense, ComponentType } from "react"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom"
import { Capacitor } from "@capacitor/core"
import { Toaster as Sonner } from "@/components/ui/sonner"
import { TooltipProvider } from "@/components/ui/tooltip"
import { AuthProvider, useAuth } from "@/contexts/AuthContext"
import ProtectedRoute from "@/components/ProtectedRoute"
import EnterpriseRoute from "@/components/EnterpriseRoute"
import AppErrorBoundary from "@/components/AppErrorBoundary"
import ErrorBoundary from "@/components/ErrorBoundary"
import { AdminImpersonationProvider } from "@/contexts/AdminImpersonationContext"
import { ExternalContractorProvider } from "@/contexts/ExternalContractorContext"
import { installGlobalErrorListeners } from "@/lib/systemLogger"
import { bootError, bootSetChunk, bootSetStage, hardReloadApp } from "@/lib/boot-debug"
import { PerfOverlay } from "@/components/perf/PerfOverlay"
import { OperationsConsole } from "@/components/operations/OperationsConsole"
import { EnterpriseObservabilityPanel } from "@/components/observability/EnterpriseObservabilityPanel"

installGlobalErrorListeners()

// NÃºcleo (eager) â€” rotas crÃ­ticas no bundle inicial
import AppLayout from "@/components/AppLayout"
import LoginPage from "@/pages/Login"
import Dashboard from "@/pages/Dashboard"

// Helper: lazy com log de erro de carga de chunk
function lazyWithLog<T extends ComponentType<any>>(
  name: string,
  loader: () => Promise<{ default: T }>,
) {
  return lazy(() =>
    {
      bootSetChunk(name, 'loading', 'Baixando chunk lazy')
      return loader()
        .then((mod) => {
          bootSetChunk(name, 'loaded', 'Chunk carregado com sucesso')
          return mod
        })
        .catch((err) => {
          const message = err instanceof Error ? err.message : String(err)
          bootSetChunk(name, 'failed', message)
          bootError(`Falha ao carregar chunk ${name}`, message)
          console.error(`[lazy] Falha ao carregar chunk "${name}":`, err)
          throw err
        })
    },
  )
}

// Lazy: pÃ¡ginas pÃ³s-login (nÃ£o-crÃ­ticas)
const DashboardObra = lazyWithLog('DashboardObra', () => import("@/pages/DashboardObra"))
const Obras = lazyWithLog('Obras', () => import("@/pages/Obras"))
const DiarioObra = lazyWithLog('DiarioObra', () => import("@/pages/DiarioObra"))
const Fotos = lazyWithLog('Fotos', () => import("@/pages/Fotos"))
const Ocorrencias = lazyWithLog('Ocorrencias', () => import("@/pages/Ocorrencias"))
const FVSPage = lazyWithLog('FVS', () => import("@/pages/FVS"))
const Pendencias = lazyWithLog('Pendencias', () => import("@/pages/Pendencias"))
const Producao = lazyWithLog('Producao', () => import("@/pages/Producao"))
const Cronograma = lazyWithLog('Cronograma', () => import("@/pages/Cronograma"))
const MapaVisual = lazyWithLog('MapaVisual', () => import("@/pages/MapaVisual"))
const Planta = lazyWithLog('Planta', () => import("@/pages/Planta"))
const Documentos = lazyWithLog('Documentos', () => import("@/pages/Documentos"))
const SST = lazyWithLog('SST', () => import("@/pages/SST"))
const APRPage = lazyWithLog('APR', () => import("@/pages/APR"))
const APTPage = lazyWithLog('APT', () => import("@/pages/APT"))
const PermissaoTrabalhoPage = lazyWithLog('PT', () => import("@/pages/PermissaoTrabalho"))
const DDSPage = lazyWithLog('DDS', () => import("@/pages/DDS"))
const AcidentesPage = lazyWithLog('Acidentes', () => import("@/pages/Acidentes"))
const SSTArquivoPage = lazyWithLog('SSTArquivo', () => import("@/pages/SSTArquivo"))
const AuditoriaPage = lazyWithLog('Auditoria', () => import("@/pages/Auditoria"))
const InspecoesPage = lazyWithLog('Inspecoes', () => import("@/pages/Inspecoes"))
const Historico = lazyWithLog('Historico', () => import("@/pages/Historico"))
const EntregaUnidades = lazyWithLog('EntregaUnidades', () => import("@/pages/EntregaUnidades"))
const FVMPage = lazyWithLog('FVM', () => import("@/pages/FVM"))
const ConcretoPage = lazyWithLog('Concreto', () => import("@/pages/Concreto"))
const RevestimentosPage = lazyWithLog('Revestimentos', () => import("@/pages/Revestimentos"))
const EstoquePage = lazyWithLog('Estoque', () => import("@/pages/Estoque"))
const AssistenteIAPage = lazyWithLog('AssistenteIA', () => import("@/pages/AssistenteIA"))
const AssistenteEmBreve = lazyWithLog('AssistenteEmBreve', () => import("@/pages/AssistenteEmBreve"))
const NotFound = lazyWithLog('NotFound', () => import("@/pages/NotFound"))
const SolicitarAcessoPage = lazyWithLog('SolicitarAcesso', () => import("@/pages/SolicitarAcesso"))
const ForgotPasswordPage = lazyWithLog('ForgotPassword', () => import("@/pages/ForgotPassword"))
const ResetPasswordPage = lazyWithLog('ResetPassword', () => import("@/pages/ResetPassword"))
const AcceptInvitePage = lazyWithLog('AcceptInvite', () => import("@/pages/AcceptInvite"))
const TeamPage = lazyWithLog('Team', () => import("@/pages/Team"))
const AdminUsuarios = lazyWithLog('AdminUsuarios', () => import("@/pages/AdminUsuarios"))
const ChatPage = lazyWithLog('Chat', () => import("@/pages/Chat"))
const RelatoriosPage = lazyWithLog('Relatorios', () => import("@/pages/Relatorios"))
const RelatorioMensalPage = lazyWithLog('RelatorioMensal', () => import("@/pages/RelatorioMensal"))
const IdentidadeEmpresa = lazyWithLog('IdentidadeEmpresa', () => import("@/pages/IdentidadeEmpresa"))
const SettingsPage = lazyWithLog('Settings', () => import("@/pages/Settings"))
const SuperAdmin = lazyWithLog('SuperAdmin', () => import("@/pages/SuperAdmin"))
const OnboardingWizard = lazyWithLog('OnboardingWizard', () => import("@/components/OnboardingWizard"))
const AguardandoAprovacao = lazyWithLog('AguardandoAprovacao', () => import("@/pages/AguardandoAprovacao"))
const DebugPage = lazyWithLog('Debug', () => import("@/pages/Debug"))
const PortalCliente = lazyWithLog('PortalCliente', () => import("@/pages/PortalCliente"))
const PortalClienteAdmin = lazyWithLog('PortalClienteAdmin', () => import("@/pages/PortalClienteAdmin"))
const SystemLogsPage = lazyWithLog('SystemLogs', () => import("@/pages/admin/SystemLogsPage"))
const MobileApp = lazyWithLog('MobileApp', () => import("@/mobile/MobileApp"))
const ConviteExterno = lazyWithLog('ConviteExterno', () => import("@/pages/ConviteExterno"))
const ContratosPage = lazyWithLog('Contratos', () => import("@/pages/Contratos"))
const DailyInPage = lazyWithLog('DailyIn', () => import("@/pages/DailyIn"))
const GestaoAVistaPage = lazyWithLog('GestaoAVista', () => import("@/pages/GestaoAVista"))
const GestaoAVistaTVPage   = lazyWithLog('GestaoAVistaTV',   () => import("@/pages/GestaoAVistaTV"))
const TVDeviceManagerPage  = lazyWithLog('TVDeviceManager',  () => import("@/pages/TVDeviceManager"))
const TVControlCenterPage  = lazyWithLog('TVControlCenter',  () => import("@/pages/TVControlCenter"))
const ExecutiveDashboard   = lazyWithLog('ExecutiveDashboard', () => import("@/pages/executive/ExecutiveDashboard"))
const WarRoom              = lazyWithLog('WarRoom',          () => import("@/pages/executive/WarRoom"))
const VisionDashboard      = lazyWithLog('VisionDashboard',  () => import("@/pages/vision/VisionDashboard"))
const TwinDashboard        = lazyWithLog('TwinDashboard',    () => import("@/pages/twin/TwinDashboard"))
const TVBurnIn             = lazyWithLog('TVBurnIn',         () => import("@/pages/TVBurnIn"))
const TelasHub             = lazyWithLog('TelasHub',          () => import("@/pages/TelasHub"))
const OperacoesPage        = lazyWithLog('Operacoes',         () => import("@/pages/Operacoes"))
const AvaliacaoFornecedores = lazyWithLog('AvaliacaoFornecedores', () => import("@/pages/AvaliacaoFornecedores"))
const RankingFornecedores = lazyWithLog('RankingFornecedores', () => import("@/pages/RankingFornecedores"))
const RankingFornecedorDetalhe = lazyWithLog('RankingFornecedorDetalhe', () => import("@/pages/RankingFornecedorDetalhe"))
const SemanaOperacional    = lazyWithLog('SemanaOperacional', () => import("@/pages/SemanaOperacional"))

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
    },
  },
})

function SplashTimeoutFallback() {
  const [timedOut, setTimedOut] = useState(false)

  useEffect(() => {
    bootSetStage('Montando app...', 'Aguardando carregamento do mÃ³dulo atual')
    const timer = window.setTimeout(() => {
      setTimedOut(true)
      bootError('Timeout no Suspense', 'Um mÃ³dulo demorou mais de 15s para carregar')
    }, 15000)

    return () => window.clearTimeout(timer)
  }, [])

  if (!timedOut) return <FullScreenLoader />

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-background px-6 text-foreground">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 text-center shadow-sm">
        <img src="/construzilla-logo.png?v=6" alt="Construzilla" className="mx-auto mb-3 h-auto w-24 object-contain" />
        <h2 className="text-lg font-extrabold">O app demorou para carregar</h2>
        <p className="mt-2 text-sm text-muted-foreground">Se algum chunk travou no mobile, abra <strong>/debug</strong> ou tente recarregar limpando o cache.</p>
        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:justify-center">
          <button className="rounded-lg bg-primary px-4 py-2 text-sm font-bold text-primary-foreground" onClick={() => void hardReloadApp()} type="button">Tentar novamente</button>
          <button className="rounded-lg border border-border bg-secondary px-4 py-2 text-sm font-semibold text-secondary-foreground" onClick={() => window.location.assign('/debug')} type="button">Abrir diagnÃ³stico</button>
        </div>
      </div>
    </div>
  )
}

function FullScreenLoader() {
  return (
    <div className="fixed inset-0 flex items-center justify-center" style={{ background: 'hsl(var(--bg0))' }}>
      <div className="text-center animate-fade-up">
        <img src="/construzilla-logo.png?v=6" alt="Construzilla" className="inline-block object-contain mb-3" style={{ maxWidth: 120, background: 'transparent', border: 'none', borderRadius: 0, boxShadow: 'none', padding: 0 }} />
        <div className="text-foreground font-extrabold text-lg">Constru<span className="text-primary">zilla</span></div>
        <div className="mt-3">
          <div className="w-5 h-5 border-2 rounded-full animate-spin mx-auto" style={{ borderColor: 'hsl(var(--amber))', borderTopColor: 'transparent' }} />
        </div>
      </div>
    </div>
  )
}

function AuthGate() {
  const { user, loading, blocked, obras, obrasLoaded, company } = useAuth()
  const [onboardingDone, setOnboardingDone] = useState(false)

  useEffect(() => {
    if (loading) {
      bootSetStage('Iniciando autenticaÃ§Ã£o...', 'Verificando sessÃ£o do usuÃ¡rio')
      return
    }

    if (!user) {
      bootSetStage('Exibindo login', 'SessÃ£o nÃ£o autenticada')
      return
    }

    if (user.status === 'pendente') {
      bootSetStage('Aguardando aprovaÃ§Ã£o', 'UsuÃ¡rio autenticado, aguardando liberaÃ§Ã£o')
      return
    }

    bootSetStage('App autenticado', 'Rotas protegidas liberadas')
  }, [loading, user])

  // /reset-password deve funcionar SEMPRE, mesmo se o usuário já tiver
  // uma sessão ativa (recovery link abre o app autenticado e cai num 404
  // se /reset-password só existir no bloco !user).
  if (typeof window !== 'undefined' && window.location.pathname === '/reset-password') {
    return (
      <Suspense fallback={<FullScreenLoader />}>
        <Routes>
          <Route path="/reset-password" element={<ResetPasswordPage />} />
        </Routes>
      </Suspense>
    )
  }

  if (loading) return <FullScreenLoader />

  if (!user) {
    return (
      <Suspense fallback={<SplashTimeoutFallback />}>
        <Routes>
          <Route path="/solicitar-acesso" element={<SolicitarAcessoPage />} />
          <Route path="/aceitar-convite" element={<AcceptInvitePage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          {/* /debug removido do bloco não autenticado — agora exige platform admin (ver bloco autenticado) */}
          <Route path="*" element={<LoginPage />} />
        </Routes>
      </Suspense>
    )
  }

  if (user.status === 'pendente') {
    return (
      <Suspense fallback={<SplashTimeoutFallback />}>
        <Routes>
          <Route path="*" element={<AguardandoAprovacao />} />
        </Routes>
      </Suspense>
    )
  }

  // Empreiteiros nunca passam pelo onboarding (tÃªm Ã¡rea prÃ³pria em /mobile)
  // e owners sem obras que sejam empreiteiros externos nÃ£o devem ficar presos aqui
  const needsOnboarding = !onboardingDone && company && obrasLoaded && obras.length === 0
    && user.company_role === 'owner'
    && user.perfil !== 'empreiteiro'
  if (needsOnboarding) {
    return (
      <Suspense fallback={<FullScreenLoader />}>
        <OnboardingWizard onComplete={() => setOnboardingDone(true)} />
      </Suspense>
    )
  }

  // Empreiteiro tem Ã¡rea exclusiva no /mobile â€” redireciona via React Router
  // para nÃ£o quebrar o histÃ³rico e evitar reload desnecessÃ¡rio.
  if (String(user.perfil || '').trim().toLowerCase() === 'empreiteiro') {
    if (!window.location.pathname.startsWith('/mobile')) {
      return <Navigate to="/mobile" replace />
    }
  }

  return (
    <AppLayout>
      <ErrorBoundary module="route">
      <Suspense fallback={<SplashTimeoutFallback />}>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/debug" element={<ProtectedRoute requirePlatformAdmin><DebugPage /></ProtectedRoute>} />
          <Route path="/obra" element={<ProtectedRoute requireRole="viewer"><DashboardObra /></ProtectedRoute>} />
          <Route path="/obras" element={<ProtectedRoute requireRole="engineer"><Obras /></ProtectedRoute>} />
          <Route path="/portal-admin/:obraId" element={<ProtectedRoute requireRole="admin"><PortalClienteAdmin /></ProtectedRoute>} />
          <Route path="/diario" element={<ProtectedRoute requireRole="viewer"><DiarioObra /></ProtectedRoute>} />
          <Route path="/fotos" element={<ProtectedRoute requireRole="viewer"><Fotos /></ProtectedRoute>} />
          <Route path="/producao" element={<ProtectedRoute requireRole="viewer"><Producao /></ProtectedRoute>} />
          <Route path="/fvs" element={<ProtectedRoute requireRole="viewer"><FVSPage /></ProtectedRoute>} />
          <Route path="/pendencias" element={<ProtectedRoute requireRole="viewer"><Pendencias /></ProtectedRoute>} />
          <Route path="/ocorrencias" element={<ProtectedRoute requireRole="viewer"><Ocorrencias /></ProtectedRoute>} />
          <Route path="/planta" element={<ProtectedRoute requireRole="engineer"><Planta /></ProtectedRoute>} />
          <Route path="/documentos" element={<ProtectedRoute requireRole="viewer"><Documentos /></ProtectedRoute>} />
          <Route path="/cronograma" element={<ProtectedRoute requireRole="viewer"><Cronograma /></ProtectedRoute>} />
          <Route path="/mapa-visual" element={<ProtectedRoute requireRole="viewer"><MapaVisual /></ProtectedRoute>} />
          <Route path="/sst" element={<ProtectedRoute requireRole="viewer"><SST /></ProtectedRoute>} />
          <Route path="/apr" element={<ProtectedRoute requireRole="viewer"><APRPage /></ProtectedRoute>} />
          <Route path="/apt" element={<ProtectedRoute requireRole="viewer"><APTPage /></ProtectedRoute>} />
          <Route path="/pt" element={<ProtectedRoute requireRole="viewer"><PermissaoTrabalhoPage /></ProtectedRoute>} />
          <Route path="/dds" element={<ProtectedRoute requireRole="viewer"><DDSPage /></ProtectedRoute>} />
          <Route path="/acidentes" element={<ProtectedRoute requireRole="viewer"><AcidentesPage /></ProtectedRoute>} />
          <Route path="/sst/arquivo" element={<ProtectedRoute requireRole="admin"><SSTArquivoPage /></ProtectedRoute>} />
          <Route path="/auditoria" element={<ProtectedRoute requireRole="admin"><AuditoriaPage /></ProtectedRoute>} />
          <Route path="/inspecoes" element={<ProtectedRoute requireRole="viewer"><InspecoesPage /></ProtectedRoute>} />
          <Route path="/historico" element={<ProtectedRoute requireRole="viewer"><Historico /></ProtectedRoute>} />
          <Route path="/entregas" element={<ProtectedRoute requireRole="viewer"><EntregaUnidades /></ProtectedRoute>} />
          <Route path="/fvm" element={<ProtectedRoute requireRole="viewer"><FVMPage /></ProtectedRoute>} />
          <Route path="/concreto" element={<ProtectedRoute requireRole="engineer"><ConcretoPage /></ProtectedRoute>} />
          <Route path="/revestimentos" element={<ProtectedRoute requireRole="engineer"><RevestimentosPage /></ProtectedRoute>} />
          <Route path="/estoque" element={<ProtectedRoute requireRole="viewer"><EstoquePage /></ProtectedRoute>} />
          <Route path="/assistente-ia" element={<ProtectedRoute requireRole="viewer"><AssistenteIAPage /></ProtectedRoute>} />
          <Route path="/assistente" element={<ProtectedRoute requireRole="viewer"><AssistenteEmBreve /></ProtectedRoute>} />
          <Route path="/contratos" element={<ProtectedRoute requireRole="engineer"><ContratosPage /></ProtectedRoute>} />
          <Route path="/dailyin" element={<ProtectedRoute requireRole="viewer"><DailyInPage /></ProtectedRoute>} />
          <Route path="/gestao-a-vista" element={<ProtectedRoute requireRole="engineer"><GestaoAVistaPage /></ProtectedRoute>} />
          <Route path="/telas" element={<ProtectedRoute requireRole="admin"><TelasHub /></ProtectedRoute>} />
          <Route path="/operacoes" element={<ProtectedRoute requireRole="admin"><OperacoesPage /></ProtectedRoute>} />
          <Route path="/avaliacao-fornecedores" element={<ProtectedRoute requireRole="viewer"><AvaliacaoFornecedores /></ProtectedRoute>} />
          <Route path="/ranking-fornecedores" element={<ProtectedRoute requireRole="viewer"><RankingFornecedores /></ProtectedRoute>} />
          <Route path="/ranking-fornecedores/:id" element={<ProtectedRoute requireRole="viewer"><RankingFornecedorDetalhe /></ProtectedRoute>} />
          <Route path="/semana-operacional" element={<ProtectedRoute requireRole="viewer"><SemanaOperacional /></ProtectedRoute>} />

          <Route path="/executive" element={<EnterpriseRoute><ExecutiveDashboard /></EnterpriseRoute>} />
          <Route path="/executive/war-room" element={<EnterpriseRoute><WarRoom /></EnterpriseRoute>} />
          <Route path="/vision/cameras" element={<EnterpriseRoute><VisionDashboard /></EnterpriseRoute>} />
          <Route path="/twin" element={<EnterpriseRoute><TwinDashboard /></EnterpriseRoute>} />
          <Route path="/rdo" element={<Navigate to="/diario" replace />} />
          <Route path="/dashboard" element={<Navigate to="/" replace />} />
          <Route path="/equipe" element={<ProtectedRoute requireRole="engineer"><TeamPage /></ProtectedRoute>} />
          <Route path="/chat" element={<ProtectedRoute requireRole="viewer"><ChatPage /></ProtectedRoute>} />
          <Route path="/relatorios" element={<ProtectedRoute requireRole="viewer"><RelatoriosPage /></ProtectedRoute>} />
          <Route path="/relatorio-mensal" element={<ProtectedRoute requireRole="engineer"><RelatorioMensalPage /></ProtectedRoute>} />
          <Route path="/admin/usuarios" element={<ProtectedRoute requireRole="admin"><AdminUsuarios /></ProtectedRoute>} />
          <Route path="/identidade" element={<ProtectedRoute requireRole="admin"><IdentidadeEmpresa /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute requireRole="admin"><SettingsPage /></ProtectedRoute>} />
          <Route path="/superadmin" element={<ProtectedRoute requirePlatformAdmin><SuperAdmin /></ProtectedRoute>} />
          <Route path="/admin/system-logs" element={<ProtectedRoute requirePlatformAdmin><SystemLogsPage /></ProtectedRoute>} />
          <Route path="/admin/erros" element={<ProtectedRoute requirePlatformAdmin><SystemLogsPage /></ProtectedRoute>} />
          <Route path="/aceitar-convite" element={<AcceptInvitePage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      </ErrorBoundary>
    </AppLayout>
  )
}

// Quando rodando como app nativo (APK/IPA), forÃ§a a interface mobile
const isNative = Capacitor.isNativePlatform()

const App = () => (
  <AppErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Sonner />
        <PerfOverlay />
        <EnterpriseObservabilityPanel />
        <OperationsConsole />
        <BrowserRouter>
          <Routes>
            <Route path="/portal/*" element={<Suspense fallback={<SplashTimeoutFallback />}><PortalCliente /></Suspense>} />
            <Route path="/mobile/*" element={<Suspense fallback={<SplashTimeoutFallback />}><MobileApp /></Suspense>} />
            <Route path="/convite/:token" element={<Suspense fallback={<SplashTimeoutFallback />}><ConviteExterno /></Suspense>} />
            <Route path="/tv/burnin"  element={<Suspense fallback={<SplashTimeoutFallback />}><TVBurnIn /></Suspense>} />
            <Route path="/tv/:obraId" element={<Suspense fallback={<SplashTimeoutFallback />}><GestaoAVistaTVPage /></Suspense>} />
            <Route path="/tv/admin"   element={<Suspense fallback={<SplashTimeoutFallback />}><AuthProvider><AdminImpersonationProvider><ExternalContractorProvider><ProtectedRoute requireRole="admin"><TVDeviceManagerPage /></ProtectedRoute></ExternalContractorProvider></AdminImpersonationProvider></AuthProvider></Suspense>} />
            <Route path="/tv/control" element={<Suspense fallback={<SplashTimeoutFallback />}><AuthProvider><AdminImpersonationProvider><ExternalContractorProvider><ProtectedRoute requireRole="admin"><TVControlCenterPage /></ProtectedRoute></ExternalContractorProvider></AdminImpersonationProvider></AuthProvider></Suspense>} />
            <Route path="/debug" element={<Suspense fallback={<SplashTimeoutFallback />}><AuthProvider><AdminImpersonationProvider><ExternalContractorProvider><ProtectedRoute requirePlatformAdmin><DebugPage /></ProtectedRoute></ExternalContractorProvider></AdminImpersonationProvider></AuthProvider></Suspense>} />
            {/* APK nativo: redireciona tudo para /mobile */}
            {isNative && <Route path="*" element={<Navigate to="/mobile" replace />} />}
            {!isNative && <Route path="*" element={<AuthProvider><AdminImpersonationProvider><ExternalContractorProvider><AuthGate /></ExternalContractorProvider></AdminImpersonationProvider></AuthProvider>} />}
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </AppErrorBoundary>
)

export default App

